import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ShoppingBag, FileCheck2, MessageSquare } from "lucide-react";
import { http } from "@/lib/api";

const ICONS = { purchase: ShoppingBag, intake: FileCheck2, message: MessageSquare };

export default function NotificationBell() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ unread: 0, events: [] });
  const ref = useRef(null);

  const load = () => http.get("/operator/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onClick); };
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread > 0) {
      await http.post("/operator/notifications/seen").catch(() => {});
      setData((d) => ({ ...d, unread: 0 }));
    }
  };

  const ago = (iso) => {
    if (!iso) return "";
    const s = Math.floor((Date.now() - new Date(iso)) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell" onClick={toggle} className="relative text-[#6B665E] hover:text-[#1A1918] transition-colors">
        <Bell size={18} />
        {data.unread > 0 && (
          <span data-testid="notification-count" className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 grid place-items-center text-[9px] font-bold bg-[#9F634E] text-white rounded-full">{data.unread}</span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel" className="absolute right-0 mt-3 w-80 max-w-[calc(100vw-2rem)] bg-white border border-[#E5E1D8] rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-[#F0ECE4] text-sm font-medium text-[#1A1918]">Notifications</div>
          <div className="max-h-96 overflow-y-auto">
            {data.events.length === 0 ? (
              <div className="px-4 py-8 text-center text-[#8A998C] text-sm">No activity yet.</div>
            ) : data.events.map((e, i) => {
              const Icon = ICONS[e.type] || Bell;
              return (
                <button key={i} onClick={() => { setOpen(false); if (e.customer_id) nav(`/operator/customer/${e.customer_id}`); }}
                  className="w-full text-left px-4 py-3 border-b border-[#F7F4EE] last:border-0 hover:bg-[#FAF8F3] flex gap-3">
                  <span className="w-7 h-7 rounded-full bg-[#9F634E]/10 grid place-items-center shrink-0"><Icon size={13} className="text-[#9F634E]" /></span>
                  <span className="min-w-0">
                    <span className="block text-[#1A1918] text-sm">{e.title}</span>
                    <span className="block text-[#6B665E] text-xs truncate">{e.detail}</span>
                    <span className="block text-[#A39E98] text-[11px] mt-0.5">{ago(e.at)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
